import React from 'react';
import {motion} from 'framer-motion';
import {config} from '../../config/jewelleryLandingConfig';
import SafeIcon from '../../common/SafeIcon';
import {FiPlay,FiImage,FiCamera,FiCheck} from 'react-icons/fi';

const scrollToContact=(e)=> {
  e.preventDefault();
  const contactSection=document.getElementById('contact');
  if (contactSection) {
    contactSection.scrollIntoView({behavior: 'smooth'});
  }
};

const Hero=()=> {
  return (
    <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
      {/* Luxury background glow */}
      <div className="luxury-glow-frame absolute inset-0 pointer-events-none"></div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left Column - Copy & CTA */}
        <motion.div
          initial={{opacity: 0,y: 30}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.8,ease: "easeOut",delay: 0.1}}
          className="max-w-xl luxury-fade-up"
        >
          <div className="luxury-glass-pill mb-8">
            <span className="w-2 h-2 rounded-full bg-gold mr-2 animate-pulse"></span>
            Premium jewellery visuals by BPA Solutions
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-[4.2rem] leading-[1.1] luxury-serif-heading mb-8">
            Turn Simple Jewellery Photos Into Premium Visuals Your Customers Will Remember
          </h1>
          <p className="text-lg md:text-xl text-muted font-light leading-relaxed mb-10 text-balance">
            At BPA Solutions Sydney, we turn simple looking jewellery photos, including mobile phone photos, into premium product images, realistic model try-on visuals and professional marketing videos for your website, social media, advertising and customer enquiries.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <button onClick={scrollToContact} className="luxury-gold-button luxury-gold-sweep">
              Request a Visual Sample
            </button>
            <a href="#proof" className="luxury-subtle-button">
              See the Proof
            </a>
          </div>
          <div className="flex flex-col sm:flex-row gap-6 pt-8 border-t border-champagne/50">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-espresso text-sm font-medium">
                <SafeIcon icon={FiCheck} className="text-gold" />
                DFY
              </div>
              <p className="text-xs text-muted">Creative production handled entirely by our team.</p>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-espresso text-sm font-medium">
                <SafeIcon icon={FiCheck} className="text-gold" />
                3 Proof Sets
              </div>
              <p className="text-xs text-muted">Raw reference,polished model & video.</p>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-espresso text-sm font-medium">
                <SafeIcon icon={FiCheck} className="text-gold" />
                AU Wide
              </div>
              <p className="text-xs text-muted">Created from Sydney for Australian brands.</p>
            </div>
          </div>
        </motion.div>

        {/* Right Column - Luxury Hero Board */}
        <motion.div
          initial={{opacity: 0,scale: 0.95}}
          animate={{opacity: 1,scale: 1}}
          transition={{duration: 1,delay: 0.2}}
          className="relative w-full h-[650px] luxury-hover-lift"
        >
          <div className="luxury-dark-panel w-full h-full p-6 flex flex-col relative z-20 overflow-hidden">
            {/* Top Chip */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gradient-to-r from-espresso to-ink border border-gold/30 px-6 py-2 rounded-full shadow-goldGlow z-30">
              <span className="text-[10px] tracking-widest uppercase text-gold">Product reference to model visual to video</span>
            </div>

            {/* Subtle glow orb inside panel */}
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[300px] h-[300px] bg-gold/20 rounded-full blur-[80px] pointer-events-none luxury-glow-orb"></div>

            {/* Gold sweep animation element */}
            <div className="luxury-gold-sweep absolute inset-0 pointer-events-none"></div>

            <div className="grid grid-cols-2 gap-4 h-full relative z-10 pt-4">
              {/* Card 1: Input - Raw Reference (4:3 ratio) */}
              <div className="luxury-media-card h-full group">
                <div className="absolute top-3 left-3 bg-ink/80 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 z-10 flex items-center gap-2">
                  <SafeIcon icon={FiCamera} className="w-3 h-3 text-gold" />
                  <span className="text-[9px] uppercase tracking-widest text-soft">Input</span>
                </div>
                <img
                  src={config.proofSets[0].rawImage.src}
                  alt={config.proofSets[0].rawImage.alt || "Raw Input"}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                  loading="eager"
                />
              </div>

              {/* Card 2: Output - Premium Model Visual (4:5 portrait,dominant) */}
              <div className="luxury-media-card h-full group lg:row-span-2">
                <div className="absolute top-3 left-3 bg-ink/80 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 z-10 flex items-center gap-2">
                  <SafeIcon icon={FiImage} className="w-3 h-3 text-gold" />
                  <span className="text-[9px] uppercase tracking-widest text-soft">Output</span>
                </div>
                <img
                  src={config.proofSets[0].polishedImage.src}
                  alt={config.proofSets[0].polishedImage.alt || "Polished Output"}
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                  loading="eager"
                />
              </div>

              {/* Card 3: Detail - Close-up (4:3 ratio) */}
              <div className="luxury-media-card h-full group">
                <div className="absolute top-3 left-3 bg-ink/80 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 z-10 flex items-center gap-2">
                  <SafeIcon icon={FiCheck} className="w-3 h-3 text-gold" />
                  <span className="text-[9px] uppercase tracking-widest text-soft">Detail</span>
                </div>
                <img
                  src={config.media.transformationImage.src}
                  alt={config.media.transformationImage.alt || "Close up detail"}
                  className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
                  loading="eager"
                />
              </div>

              {/* Card 4: Motion - Try-on Video (wide horizontal) */}
              <div className="luxury-media-card h-full group lg:col-span-2">
                <div className="absolute top-3 left-3 bg-ink/80 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 z-10 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                  <span className="text-[9px] uppercase tracking-widest text-soft">Motion</span>
                </div>
                <video
                  src={config.proofSets[0].video.src}
                  poster={config.proofSets[0].video.poster}
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;